<template>
  <div v-loading="loading">
    <label-description-toggle
      :value="value.random_variant_allocation"
      :label-text="$t('Campaign_VariantSetup_Random_Variant_Allocation')"
      :description-text="
        $t('Campaign_VariantSetup_Random_Variant_Allocation_Description')
      "
      info-icon
      :tooltip-content="
        $t('Campaign_VariantSetup_Random_Variant_Allocation_ToolTip')
      "
      size="medium"
      style="margin-bottom: 20px;"
      @change="randomAllocationChange"
    />
    <label-description-toggle
      :value="value.custom_variant_share"
      :label-text="$t('Campaign_VariantSetup_Split_Percentage_Toggle_Label')"
      :description-text="
        $t('Campaign_VariantSetup_Split_Percentage_Toggle_Description')
      "
      size="medium"
      style="margin-bottom: 20px;"
      @change="customVariantShareChange"
      :disabled="!!variantWinner"
      :disabled-reason-tooltip="
        $t('Campaign_VariantSetup_Split_Percentage_Toggle_Disabled_Reason')
      "
    />
    <el-table
      :data="value.variants"
      :class="{ 'table-ct': !value.promotion, 'variants-table': true }"
      :key="variantsTableRefreshKey"
    >
      <el-table-column :width="30">
        <template slot-scope="scope">
          <el-tooltip
            placement="top"
            effect="light"
            v-if="variantWinner && value.variants[scope.$index].key === 'W'"
          >
            <div slot="content">
              {{
                $t('Campaign_VariantSetup_SelectAsWinner_Tooltip', {
                  who: variantWinner.user_name,
                  date: winnerSelectedDate
                })
              }}
            </div>
            <i class="winner el-icon-trophy" />
          </el-tooltip>
          <div v-else class="keyCol">
            {{ value.variants[scope.$index].key }}
          </div>
        </template>
      </el-table-column>
      <el-table-column :label="$t('Campaign_VariantSetup_Description')">
        <template slot-scope="scope">
          <el-input v-model="value.variants[scope.$index].title" />
        </template>
      </el-table-column>

      <el-table-column
        v-if="value.promotion"
        :label="$t('Campaign_VariantSetup_SelectPromotion')"
        class="promotionColumn"
      >
        <template slot-scope="scope">
          <promotion-setup
            v-model="value.variants[scope.$index]"
            :compact="true"
          />
        </template>
      </el-table-column>

      <el-table-column
        :width="100"
        align="center"
        :label="$t('Campaign_VariantSetup_Active')"
      >
        <template slot-scope="scope">
          <el-checkbox
            :disabled="!!variantWinner || !!value.custom_variant_share"
            :true-label="1"
            :false-label="0"
            class="checkboxBtn"
            v-model="value.variants[scope.$index].active"
            @change="
              selection => {
                showWarning(selection, scope.$index)
              }
            "
          />
        </template>
      </el-table-column>
      <el-table-column
        v-if="pickAWinnerManualEnabled && !isCreateCampaignRoute"
        :width="170"
        align="center"
        :label="$t('Campaign_VariantSetup_SelectAsWinner_Label')"
      >
        <template slot-scope="scope">
          <el-button
            v-if="!variantWinner"
            :disabled="!enableWinnerColumn"
            type="primary"
            plain
            icon="el-icon-trophy"
            size="mini"
            @click="selectWinnerDialog(scope.$index)"
          >
            {{ $t('Campaign_VariantSetup_SelectAsWinner') }}
          </el-button>
          <el-tooltip
            v-else-if="
              variantWinner &&
                variantWinner.key === value.ab_variants[scope.$index]
            "
            placement="top"
            effect="light"
          >
            <div slot="content">
              {{
                $t('Campaign_VariantSetup_SelectAsWinner_Tooltip', {
                  who: variantWinner.user_name,
                  date: winnerSelectedDate
                })
              }}
            </div>
            <i class="el-icon-trophy" />
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        :width="170"
        align="center"
        :label="$t('Campaign_VariantSetup_Split_Percentage_Column_Header')"
        v-if="value.custom_variant_share"
      >
        <template v-slot="scope">
          <el-input-number
            v-model="value.variants[scope.$index].split_percentage"
            size="small"
            controls-position="right"
            placeholder="%"
            :min="0"
            :max="100"
            :disabled="!!variantWinner"
            v-if="!isWinningVariant(scope.$index)"
          />
        </template>
      </el-table-column>
      <el-table-column
        :width="100"
        align="center"
        :label="$t('Campaign_VariantSetup_RemoveVariant')"
      >
        <template slot-scope="scope">
          <div class="iconCt">
            <el-tooltip :is="!allowVariantDelete ? 'el-tooltip' : 'div'">
              <div
                style="text-align: center"
                v-if="!allowVariantDelete"
                slot="content"
                v-html="$t('Campaign_VariantSetup_RemoveVariant_Tooltip')"
              />
              <icon
                v-if="value.variants.length !== 1"
                class="icon"
                :class="{ 'not-allowed': !allowVariantDelete }"
                glyph="bin"
                @click="removeVariantDialog(scope.$index)"
              />
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <el-button
      type="primary"
      class="addBtn"
      @click="addVariant"
      plain
      icon="el-icon-circle-plus-outline"
      :disabled="value.variants.length > 9 || !!variantWinner"
    >
      {{ $t('Campaign_VariantSetup_AddVariant') }}
    </el-button>

    <el-table class="cgTable" :data="control_group">
      <el-table-column
        :width="170"
        :label="$t('Campaign_VariantSetup_CG_Title')"
      >
        <template slot-scope="scope">
          <el-input-number
            size="small"
            controls-position="right"
            :min="0"
            :max="100"
            placeholder="%"
            v-model="control_group[scope.$index].ab_cg_size"
            @change="cgChange"
          />
        </template>
      </el-table-column>
      <el-table-column>
        <div class="cgDescription">
          {{ $t('Campaign_VariantSetup_CG_Description') }}
        </div>
      </el-table-column>
      <el-table-column
        :width="100"
        align="center"
        :label="$t('Campaign_VariantSetup_Active')"
      >
        <template slot-scope="scope">
          <el-checkbox
            class="checkboxBtn"
            :true-label="1"
            :false-label="0"
            v-model="control_group[scope.$index].ab_cg"
            @change="selection => (value.ab_cg = selection)"
          />
        </template>
      </el-table-column>
    </el-table>

    <el-table class="pawTable" v-if="pickAWinnerEnabled" :data="pick_a_winner">
      <el-table-column
        style="white-space: break-word"
        :label="$t('CampaignForm_Setup_PaW')"
      >
        <template slot-scope="scope">
          <div
            v-if="pick_a_winner[scope.$index] === 'sample_percentage'"
            style="line-height: 1.5;"
          >
            <div class="title">
              {{ $t('CampaignForm_Setup_PaW_Test_Percentage_Desc') }}
            </div>
            <div class="description">
              {{
                value.ab_variants.length > 1
                  ? $t('CampaignForm_Setup_PaW_Test_Percentage_Info', {
                      percent: 100 - tempPaWTestPercentage
                    })
                  : $t('CampaignForm_Setup_PaW_Test_Percentage_Info_NoUsage')
              }}
            </div>
          </div>
          <div
            v-if="pick_a_winner[scope.$index] === 'pick_winner'"
            style="line-height: 1.5;"
          >
            <div class="title">
              {{ $t('CampaignForm_Setup_PickWinnerVariant_Checkbox_Title') }}
            </div>
            <div class="description">
              {{ $t('CampaignForm_Setup_PickWinnerVariant_Checkbox_Desc') }}
            </div>
          </div>
          <div
            v-if="pick_a_winner[scope.$index] === 'pick_winner_after_n'"
            style="line-height: 1.5;"
          >
            <div class="title">
              {{ $t('CampaignForm_Setup_PaW_Test_Duration') }}
            </div>
            <div class="description">
              {{ $t('CampaignForm_Setup_PaW_Test_Duration_Desc') }}
            </div>
          </div>
          <div
            v-if="pick_a_winner[scope.$index] === 'pick_winner_sample_channel'"
            style="line-height: 1.5;"
          >
            <div class="title">
              {{ $t('CampaignForm_Setup_PaW_Measured_Metric') }}
            </div>
            <div class="description">
              {{ $t('CampaignForm_Setup_PaW_Measured_Metric_Desc') }}
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column width="255">
        <template slot-scope="scope">
          <el-input-number
            v-if="pick_a_winner[scope.$index] === 'sample_percentage'"
            v-model="tempPaWTestPercentage"
            :min="1"
            :max="100"
            :step="1"
            ref="test_sample_percent"
            placeholder="%"
            size="small"
            controls-position="right"
            :disabled="
              !!variantWinner ||
                hasScheduledSample ||
                !hasMoreThanOneActiveVariants
            "
          />
          <toggle
            v-if="pick_a_winner[scope.$index] === 'pick_winner'"
            :disabled="!hasMoreThanOneActiveVariants || !!variantWinner"
            v-model="value.automatically_pick_winner"
          />
          <div v-if="pick_a_winner[scope.$index] === 'pick_winner_after_n'">
            <el-input-number
              v-model="value.automatically_pick_winner_after_n"
              :min="1"
              :step="1"
              size="small"
              controls-position="right"
              style="width: 6rem; margin-right: .5rem;"
              :disabled="!!variantWinner || !value.automatically_pick_winner"
            />
            <el-select
              v-model="value.automatically_pick_winner_after_period"
              style="width: 7.5rem;"
              size="small"
              :disabled="!!variantWinner || !value.automatically_pick_winner"
            >
              <el-option
                v-for="(option, index) in periodOptions"
                :key="index"
                :label="option"
                :value="option"
              />
            </el-select>
          </div>
          <div
            v-if="pick_a_winner[scope.$index] === 'pick_winner_sample_channel'"
          >
            <el-tooltip
              :content="
                $t('CampaignForm_Setup_PickWinner_Valid_Channels_Warning')
              "
              placement="right"
              :disabled="availableMetrics.length > 0"
            >
              <el-select
                v-model="selectedMetric"
                style="width: 14.3rem;"
                size="small"
                :disabled="
                  !availableMetrics.length ||
                    !!variantWinner ||
                    !value.automatically_pick_winner
                "
              >
                <el-option
                  v-for="(option, index) in availableMetrics"
                  :key="index"
                  :label="option.name"
                  :value="index"
                />
              </el-select>
            </el-tooltip>
          </div>
          <el-tooltip
            :content="
              $t('CampaignForm_Setup_PickWinner_Enable_Variant_Warning')
            "
            placement="right"
            v-if="
              !hasMoreThanOneActiveVariants &&
                ['sample_percentage', 'pick_winner'].includes(
                  pick_a_winner[scope.$index]
                )
            "
          >
            <i
              class="ico el-icon-warning-outline"
              style="vertical-align: sub; margin-left: 0.5rem; font-size: 16px"
            ></i>
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import { EventBus } from '@/core/EventBus'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import _ from 'lodash'
import moment from 'moment'
import Toggle from '@/common/components/form/Toggle'
import { campaignTypeEnum } from '@/modules/campaigns/enum/CampaignTypeEnum'
import { campaignTriggerTypes } from '@/modules/campaign-manage/enum/CampaignTriggerTypesEnum'
import LabelDescriptionToggle from '@/common/components/form/LabelDescriptionToggle.vue'
import PromotionSetup from './PromotionSetup.vue'

export default {
  name: 'VariantSetup',
  components: { PromotionSetup, LabelDescriptionToggle, Toggle },
  props: {
    value: {
      required: true
    }
  },

  data() {
    return {
      loading: false,
      periodOptions: ['seconds', 'minutes', 'hours', 'days', 'weeks'],
      selectedMetric: null,
      campaignTypeEnum,
      tempPaWTestPercentage: 100,
      randomAllocation: 0,
      variantsTableRefreshKey: 0
    }
  },

  mounted() {
    if (this.pickAWinnerEnabled) {
      this.setSelectedMetric()
      this.tempPaWTestPercentage = this.value.automatically_pick_winner_sample_percentage
    }
  },

  computed: {
    variantWinner() {
      return this.value.variants.find(variant => variant.winner)
    },
    winnerSelectedDate() {
      if (this.variantWinner && this.variantWinner.winner_create_time) {
        return moment(this.variantWinner.winner_create_time * 1000).format(
          'DD-MMM-YYYY HH:mm'
        )
      }
      return null
    },
    showWinnerColumn() {
      return this.hasMoreThanOneActiveVariants
    },
    enableWinnerColumn() {
      return this.showWinnerColumn && this.value.active === 1
    },
    hasMoreThanOneActiveVariants() {
      return this.value.variants.filter(v => v.active === 1).length > 1
    },
    pickAWinnerEnabled() {
      return (
        this.value.type !== campaignTypeEnum.TYPE_WORKFLOW &&
        this.value.type !== campaignTypeEnum.TYPE_INAPP &&
        this.value.trigger <= campaignTriggerTypes.TRIGGER_API
      )
    },
    pickAWinnerManualEnabled() {
      return (
        this.value.type !== campaignTypeEnum.TYPE_WORKFLOW ||
        (this.value.type === campaignTypeEnum.TYPE_WORKFLOW &&
          this.value.trigger > campaignTriggerTypes.TRIGGER_NONE)
      )
    },
    autoPickAWinnerEnabled() {
      return (
        this.value.type !== campaignTypeEnum.TYPE_WORKFLOW &&
        this.value.type !== campaignTypeEnum.TYPE_INAPP &&
        this.value.trigger <= campaignTriggerTypes.TRIGGER_API
      )
    },
    pickAWinnerTestPercentEnabled() {
      return (
        this.value.type !== campaignTypeEnum.TYPE_WORKFLOW &&
        this.value.type !== campaignTypeEnum.TYPE_INAPP &&
        this.value.trigger === campaignTriggerTypes.TRIGGER_NONE
      )
    },
    hasScheduledSample() {
      return this.value.automatically_pick_winner_schedule_status !== null
    },
    allowVariantDelete() {
      return (
        !this.value.id || this.value.draft || this.value.is_campaign_template
      )
    },
    control_group() {
      return [
        {
          ab_cg: this.value.ab_cg,
          ab_cg_size: this.value.ab_cg_size
        }
      ]
    },
    pick_a_winner() {
      let pawSettings = this.autoPickAWinnerEnabled
        ? ['pick_winner', 'pick_winner_after_n', 'pick_winner_sample_channel']
        : []

      if (this.pickAWinnerTestPercentEnabled) {
        pawSettings = ['sample_percentage', ...pawSettings]
      }

      return pawSettings
    },
    availableMetrics() {
      const options = []

      if (this.value.android_push || this.value.ios_push) {
        options.push({
          name: this.$t('Campaign_VariantSetup_PickWinner_Metrics_ClickRate', {
            metric: this.$t(
              'Campaign_VariantSetup_PickWinner_MetricsOption_MobilePush'
            )
          }),
          channel: 'mobile_push',
          type: 'clicks'
        })
      }

      if (this.value.web_push) {
        options.push({
          name: this.$t('Campaign_VariantSetup_PickWinner_Metrics_ClickRate', {
            metric: this.$t(
              'Campaign_VariantSetup_PickWinner_MetricsOption_WebPush'
            )
          }),
          channel: 'web_push',
          type: 'clicks'
        })
      }

      if (this.value.email) {
        options.push({
          name: this.$t('Campaign_VariantSetup_PickWinner_Metrics_OpenRate', {
            metric: this.$t(
              'Campaign_VariantSetup_PickWinner_MetricsOption_Email'
            )
          }),
          channel: 'email',
          type: 'opens'
        })

        options.push({
          name: this.$t('Campaign_VariantSetup_PickWinner_Metrics_ClickRate', {
            metric: this.$t(
              'Campaign_VariantSetup_PickWinner_MetricsOption_Email'
            )
          }),
          channel: 'email',
          type: 'clicks'
        })
      }

      if (this.value.inbox) {
        options.push({
          name: this.$t('Campaign_VariantSetup_PickWinner_Metrics_OpenRate', {
            metric: this.$t(
              'Campaign_VariantSetup_PickWinner_MetricsOption_Inbox'
            )
          }),
          channel: 'inbox',
          type: 'opens'
        })

        options.push({
          name: this.$t('Campaign_VariantSetup_PickWinner_Metrics_ClickRate', {
            metric: this.$t(
              'Campaign_VariantSetup_PickWinner_MetricsOption_Inbox'
            )
          }),
          channel: 'inbox',
          type: 'clicks'
        })
      }

      if (this.value.facebook) {
        options.push({
          name: this.$t('Campaign_VariantSetup_PickWinner_Metrics_ClickRate', {
            metric: this.$t(
              'Campaign_VariantSetup_PickWinner_MetricsOption_Facebook'
            )
          }),
          channel: 'facebook',
          type: 'clicks'
        })
      }

      if (this.value.whatsapp) {
        options.push({
          name: this.$t('Campaign_VariantSetup_PickWinner_Metrics_ClickRate', {
            metric: this.$t(
              'Campaign_VariantSetup_PickWinner_MetricsOption_Whatsapp'
            )
          }),
          channel: 'whatsapp',
          type: 'clicks'
        })
      }

      return options
    },
    isCreateCampaignRoute() {
      const createCampaignRoutes = ['CreateCampaign', 'CreateTemplateCampaign']
      return createCampaignRoutes.includes(this.$route.name)
    }
  },

  methods: {
    showWarning(checked, index) {
      if (this.value.active && !checked) {
        this.$msgbox({
          title: this.$t('Campaign_VariantSetup_Warning_Title'),
          message: this.$t('Campaign_VariantSetup_Warning_Description'),
          showCancelButton: true,
          confirmButtonText: this.$t('Campaign_VariantSetup_Warning_Confirm'),
          cancelButtonText: this.$t('Campaign_VariantSetup_Warning_Cancel'),
          center: true
        }).catch(() => {
          this.value.variants[index].active = 1
        })
      }
    },
    setSelectedMetric() {
      const index = this.availableMetrics.findIndex(
        item =>
          item.channel ===
            this.value.automatically_pick_winner_sample_channel &&
          item.type === this.value.automatically_pick_winner_sample_type
      )

      this.selectedMetric = index !== -1 ? index : null
    },
    selectWinnerDialog(index) {
      const variant = this.value.ab_variants[index]
      this.$msgbox({
        title: this.$t('Campaign_VariantSetup_SelectAsWinner_Popup_Title'),
        message:
          this.value.trigger === 0
            ? this.$t(
                'Campaign_VariantSetup_SelectAsWinner_Popup_Message_Scheduled',
                { variant }
              )
            : this.$t(
                'Campaign_VariantSetup_SelectAsWinner_Popup_Message_Triggered',
                { variant }
              ),
        showCancelButton: true,
        confirmButtonText: this.$t(
          'Campaign_VariantSetup_SelectAsWinner_Popup_Select'
        ),
        cancelButtonText: this.$t(
          'Campaign_VariantSetup_SelectAsWinner_Popup_Cancel'
        ),
        dangerouslyUseHTMLString: true,
        center: true
      })
        .then(() => {
          this.selectWinner(variant)
        })
        .catch(() => {})
    },
    selectWinner(variant) {
      RestApi(
        projectEndpoint({
          method: 'post',
          url: `campaigns/select-winner`,
          params: { campaign_id: this.value.id, variant: variant }
        })
      )
        .then(() => {
          this.$message({
            message: this.$t(
              'Campaign_VariantSetup_SelectAsWinner_SuccessMessage'
            ),
            type: 'success'
          })
          EventBus.$emit('force-reload-page')
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },
    addVariant() {
      // Backend is limited to 10 variants
      if (this.value.variants.length < 10) {
        const lastKey = this.value.variants[this.value.variants.length - 1].key
        const nextKey = String.fromCharCode(lastKey.charCodeAt(0) + 1)
        this.value.variants.push({
          key: nextKey,
          title: '',
          active: 1,
          promotion_id: null,
          promotion_action: null,
          promotion_params: null,
          promotion_amount: null,
          promotion_amount_type: null,
          promotion_amount_attribute_title: null,
          promotion_amount_metric_title: null,
          promotion_amount_property_title: null,
          promotion_amount_percents: null,
          promotion_amount_round: null,
          promotion_amount_min: null,
          promotion_amount_cap: null,
          promotion_amount_window: null,
          promotion_amount_daterange: null,
          promotion_duration: null
        })

        this.value.ab_variants.push(nextKey)
      }
    },

    removeVariantDialog(index) {
      if (this.variantWinner || !this.allowVariantDelete) {
        return
      }
      this.$msgbox({
        title: this.$t('Common_Popup_Delete_Title'),
        message: this.value.active
          ? this.$t('Campaign_VariantSetup_RemoveVariant_Warning')
          : this.$t('Common_Popup_Delete_Message'),
        showCancelButton: true,
        confirmButtonText: this.$t('Common_Popup_Delete_Confirm'),
        cancelButtonText: this.$t('Common_Popup_Delete_Cancel'),
        center: true
      })
        .then(() => {
          this.removeVariant(index)
        })
        .catch(() => {})
    },

    removeVariant(index) {
      // Delete variant from messages
      Object.keys(this.value.messages).forEach(ch => {
        this.$delete(this.value.messages[ch], this.value.ab_variants[index])

        this.value.ab_variants.forEach((variant, index) => {
          let nextVar = this.value.ab_variants[index + 1]
          if (
            nextVar &&
            !this.value.messages[ch].hasOwnProperty(variant) &&
            this.value.messages[ch].hasOwnProperty(nextVar)
          ) {
            let msg = _.cloneDeep(this.value.messages[ch][nextVar])
            if (msg && msg.content && msg.content.variant_key) {
              msg.content.variant_key = variant
            }
            this.$set(this.value.messages[ch], variant, msg)
            this.$delete(this.value.messages[ch], nextVar)
          }
        })
      })

      this.value.variants.splice(index, 1)
      this.value.ab_variants.splice(index, 1)

      // reset keys if the deleted variant is not last
      if (this.value.variants.length !== index) {
        this.value.ab_variants = []
        this.value.variants.forEach((variant, index) => {
          if (index === 0) {
            variant.key = 'A'
          } else {
            variant.key = String.fromCharCode(
              this.value.variants[index - 1].key.charCodeAt(0) + 1
            )
          }

          this.value.ab_variants.push(variant.key)
        })
      }

      // add an empty variant if the last record was removed
      if (this.value.variants.length === 0) {
        this.value.variants.push({
          key: 'A',
          title: '',
          active: 1,
          promotion_id: null,
          promotion_action: null,
          promotion_params: null,
          promotion_amount: null,
          promotion_amount_type: null,
          promotion_amount_attribute_title: null,
          promotion_amount_metric_title: null,
          promotion_amount_property_title: null,
          promotion_amount_percents: null,
          promotion_amount_round: null,
          promotion_amount_min: null,
          promotion_amount_cap: null,
          promotion_amount_window: null,
          promotion_amount_daterange: null,
          promotion_duration: null
        })

        this.value.ab_variants.push('A')
      }
      if (this.value.ab_variants.length <= 1) {
        this.value.automatically_pick_winner = 0
      }

      this.variantsTableRefreshKey++
    },

    cgChange(selection) {
      this.value.ab_cg_size = selection
      this.value.ab_cg = 1
    },

    randomAllocationChange(value) {
      this.value.random_variant_allocation = value
    },

    customVariantShareChange(value) {
      this.value.custom_variant_share = value
    },

    isWinningVariant(index) {
      return this.value.variants[index].key === 'W'
    }
  },

  watch: {
    tempPaWTestPercentage: {
      handler(val) {
        if (!val) {
          this.tempPaWTestPercentage = 101
          this.$nextTick(
            () =>
              (this.tempPaWTestPercentage = this.value.automatically_pick_winner_sample_percentage)
          )
          return
        }
        if (val >= 1 && val <= 100) {
          this.value.automatically_pick_winner_sample_percentage = val
          return
        }
        this.value.automatically_pick_winner_sample_percentage = 100
      }
    },
    'value.automatically_pick_winner_after_n': {
      handler(val) {
        if (!val) {
          this.value.automatically_pick_winner_after_n = 0
        }
      },
      deep: true
    },
    'value.variants': {
      handler(val) {
        if (val.filter(v => v.active === 1).length <= 1) {
          this.value.automatically_pick_winner = 0
        }
      },
      deep: true
    },
    selectedMetric: {
      handler(val) {
        let metric = val !== null ? this.availableMetrics[val] : null
        this.$set(
          this.value,
          'automatically_pick_winner_sample_channel',
          metric ? metric.channel : null
        )
        this.$set(
          this.value,
          'automatically_pick_winner_sample_type',
          metric ? metric.type : null
        )
      }
    },
    availableMetrics: {
      handler() {
        // Reset selectedMetric index
        this.setSelectedMetric()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
>>> .el-table {
  border: 1px solid #dbdee7;
  border-radius: 4px;
}

.table-ct {
  width: 50%;
}

.select {
  flex: 1;
}

.number-input {
  width: 80px !important;
  flex: 0 0 auto;
  margin-left: 10px;
}

>>> .el-input-number {
  width: 130px;
}

>>> .el-table th {
  padding: 12px 0;
  font-family: Open Sans, sans-serif;
  font-size: 14px;
  line-height: 22px;
  font-weight: 600;
  color: #4b515f;
}

>>> .el-table td {
  padding: 4px 0;
}

.variants-table >>> td {
  padding: 12px 0;
  vertical-align: top;
}

.variants-table >>> td .cell {
  display: flex;
  min-height: 40px;
  justify-content: center;
  align-items: center;
}

.cgTable,
.pawTable {
  max-width: 800px;
  width: 50%;
  margin-top: 20px;
}

.pawDescription,
.cgDescription {
  white-space: normal;
  margin-left: -10px;
}

.keyCol {
  margin-left: 5px;
}
.winner {
  margin-left: 3px;
}

.iconCt {
  display: flex;
  justify-content: center;
}

.icon {
  display: flex;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: #a7acba;
}

.icon:hover {
  cursor: pointer;
}

.not-allowed,
.not-allowed:hover {
  cursor: not-allowed;
}

.checkboxBtn {
  display: flex;
  justify-content: center;
}

>>> .el-input {
  width: 100%;
}

>>> .el-select {
  width: 100%;
}

>>> .is-focus .el-checkbox__inner {
  border-color: #606266;
}

>>> .el-checkbox__inner:hover {
  border-color: #1d87c8;
}

>>> .is-checked .el-checkbox__inner {
  background-color: #1d87c8;
  border-color: #1d87c8;
}

.addBtn {
  margin-top: 20px;
}

>>> .pawTable .title {
  color: #5c6374;
}

>>> .pawTable .description {
  color: #7e8596;
  font-size: 12px;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/setup/VariantSetup.vue